import React, { useMemo, useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { CellClickedEvent } from "ag-grid-community";
import { Grid, Text, AgGrid, AgGridReact } from "@jsq/ds/core";
import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import { DATA_VAULT_CONTEXT } from "../../../constants";
import { useSections } from "../../../CustomFields/hooks/useSections";
import { useEntityDetailModal } from "../../hooks/useEntityDetailModal";
import { useUpdateExtensionFieldValue } from "../../hooks/useUpdateExtensionFieldValue";
import { EntityDetailModal } from "../EntityDetailModal";
import {
  TextCellEditor,
  NumberCellEditor,
  DateCellEditor,
  EnumerationCellEditor,
  CurrencyCellEditor,
  PercentileCellEditor,
} from "./cellEditors";
import { ExtensionFieldCellRenderer } from "./cellRenderers";
import { useColumnDefinitions } from "./columnDefinitions";
import { DataVaultEntitiesGQLDataSource } from "./dataVaultEntitiesGQLDataSource";
import { createCellValueChangedHandler } from "./extensionFieldColumns";
import { NoRowsOverlay } from "./NoRowsOverlay";
import * as styles from "./styles_vanilla.css";

interface InvestmentEntitiesGridProps {
  /** Arena ID to fetch entities for */
  arenaId?: string;
  /** Arena name for display purposes */
  arenaName?: string;
}

/**
 * Investment Entities Grid with inline editing and modal functionality.
 * Uses server-side data source for efficient pagination and filtering via DataVaultEntities query.
 */
export const InvestmentEntitiesGrid: React.FC<InvestmentEntitiesGridProps> = () => {
  const client = useApolloClient();
  const {
    sectionsWithFields,
    tabsWithSections,
    rawExtensionFields: extensionFields,
  } = useSections(DATA_VAULT_CONTEXT);

  const { modalState, openModal, closeModal } = useEntityDetailModal();
  const [_localEdits, setLocalEdits] = useState<
    Record<string, Record<string, ExtensionFieldValueObjectType>>
  >({});
  const [gridApi, setGridApi] = useState<AgGrid.GridApi | null>(null);

  // Create server-side data source for grid
  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new DataVaultEntitiesGQLDataSource(client, 20, extensionFields);
  }, [client, extensionFields]);

  useEffect(() => {
    if (gridApi && gridDataSource) {
      gridApi.setGridOption("serverSideDatasource", gridDataSource);
      gridApi.refreshServerSide({ purge: true });
    }
  }, [gridApi, gridDataSource]);

  // Clear local edits for a specific entity (used when modal updates are made)
  const clearEntityLocalEdits = (entityId: string) => {
    setLocalEdits((prev) => {
      const { [entityId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const { updateValue: updateExtensionFieldValue } = useUpdateExtensionFieldValue(null, {
    invalidateCache: false, // Don't invalidate cache for inline editing - rely on local state
  });
  const handleCellValueChanged = createCellValueChangedHandler(
    updateExtensionFieldValue,
    setLocalEdits,
  );

  // Generate column definitions with inline editing support
  const columnDefs = useColumnDefinitions(
    sectionsWithFields,
    tabsWithSections,
    extensionFields,
    handleCellValueChanged,
  );

  // Handle cell click to control modal opening behavior
  const handleCellClicked = (event: CellClickedEvent) => {
    const colId = event.column?.getColId();
    const isExtensionField = colId?.startsWith("extensionField_");
    const isEditable = event.colDef?.editable;

    // If clicking on an editable extension field, don't trigger row click
    if (isExtensionField && isEditable) {
      return;
    }

    // For non-extension field cells (entity name, ID), trigger the modal
    const entityData = event.data;
    if (entityData) {
      openModal(entityData);
    }
  };

  const noRowsTemplate = `<span>No entities found</span>`;

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    suppressCellFocus: false,
    suppressRowClickSelection: true,
    suppressContextMenu: true,
    stopEditingWhenCellsLoseFocus: true,
    onGridReady(event: AgGrid.GridReadyEvent) {
      setGridApi(event.api);
    },
    onCellClicked: handleCellClicked,
    onCellValueChanged: handleCellValueChanged,
  };

  const defaultColumnOptions = {
    editable: false,
    flex: 1,
    minWidth: 60,
    suppressHeaderMenuButton: false,
    enableCellChangeFlash: true,
  };

  const onFilterChanged = (event: AgGrid.FilterChangedEvent) => {
    const changedColumns = event.columns;
    const arenaFilterChanged = changedColumns?.some((col) => col.getColId() === "arena");

    if (arenaFilterChanged) {
      const arenaFilterModel = event.api.getFilterModel()?.arena;
      const hasArenaFilter =
        arenaFilterModel && (!Array.isArray(arenaFilterModel) || arenaFilterModel.length > 0);

      event.api.setFilterModel({
        ...event.api.getFilterModel(),
        name: hasArenaFilter ? event.api.getFilterModel()?.name : null,
      });
    }

    // De-select all rows when the filter changes
    event.api.deselectAll();
  };

  // Error state for extension fields
  if (!extensionFields) {
    return (
      <div className={styles.errorContainer}>
        <Text variant="regular-md" color="danger600">
          Failed to load extension fields
        </Text>
      </div>
    );
  }

  return (
    <>
      <div className={styles.gridContainer} data-testid="investment-entities-grid">
        <div className={styles.gridWrapper}>
          <Grid
            cacheBlockSize={20}
            columnDefs={columnDefs}
            defaultColDef={defaultColumnOptions}
            getRowId={(params) => params.data?.id}
            gridId="investment_entities"
            gridOptions={gridOptions}
            onFilterChanged={onFilterChanged}
            overlayNoRowsTemplate={noRowsTemplate}
            pagination
            paginationPageSize={20}
            paginationPageSizeSelector={false}
            reactiveCustomComponents
            rowModelType="serverSide"
            rowSelection="single"
            serverSideDatasource={gridDataSource}
            sideBar={{
              toolPanels: [
                {
                  id: "filters",
                  labelDefault: "Filters",
                  labelKey: "filters",
                  iconKey: "filter",
                  toolPanel: "agFiltersToolPanel",
                  toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: false,
                    suppressColumnSelectAll: false,
                    suppressExpandAll: true,
                  },
                },
                {
                  id: "columns",
                  labelDefault: "Columns",
                  labelKey: "columns",
                  iconKey: "columns",
                  toolPanel: "agColumnsToolPanel",
                  toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: false,
                    suppressColumnSelectAll: false,
                    suppressColumnExpandAll: true,
                  },
                },
              ],
              position: "right",
            }}
            suppressServerSideFullWidthLoadingRow
            noRowsOverlayComponent={NoRowsOverlay}
            components={{
              ExtensionFieldCellRenderer,
              TextCellEditor,
              NumberCellEditor,
              DateCellEditor,
              EnumerationCellEditor,
              CurrencyCellEditor,
              PercentileCellEditor,
            }}
          />
        </div>
      </div>

      {/* Entity Details Modal */}
      <EntityDetailModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        selectedEntity={modalState.selectedEntity ? modalState.selectedEntity : null}
        extensionFields={extensionFields || []}
        sectionsWithFields={sectionsWithFields}
        tabsWithSections={tabsWithSections}
        onDataRefresh={() => {
          if (modalState.selectedEntity) {
            clearEntityLocalEdits(modalState.selectedEntity.id);
          }
          if (!gridApi) return;
          gridApi.refreshServerSide({ purge: false });
        }}
      />
    </>
  );
};

export default InvestmentEntitiesGrid;
